import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;
axios.interceptors.response.use(
  (r) => r,
  (e) => {
    const url = e.config?.url || "";
    if (!url.endsWith("/auth/me")) console.warn(`[network] ${e.response?.status || "ERR"} ${e.config?.method?.toUpperCase()} ${url}`);
    return Promise.reject(e);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---- auth ----
export const authMe = () => axios.get(`${API}/auth/me`).then((r) => r.data);
export const authLogin = (email, password) =>
  axios.post(`${API}/auth/login`, { email, password }).then((r) => r.data);
export const authRegister = (email, password, name) =>
  axios.post(`${API}/auth/register`, { email, password, name, ref: localStorage.getItem("stp_ref") || "" }).then((r) => r.data);
export const authLogout = () => axios.post(`${API}/auth/logout`, {}).then((r) => r.data);
export const authGoogleSession = (sessionId) =>
  axios.post(`${API}/auth/session`, { ref: localStorage.getItem("stp_ref") || "" }, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data);

// ---- config ----
export const getConfig = () => axios.get(`${API}/config`).then((r) => r.data);

// ---- generation ----
export const generate = (payload) => axios.post(`${API}/generate`, payload).then((r) => r.data);
export const getPrompt = (id) => axios.get(`${API}/prompt/${id}`).then((r) => r.data);

export async function transcribe(blob) {
  const form = new FormData();
  form.append("audio", blob, "recording.webm");
  const res = await axios.post(`${API}/voice/transcribe`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.text;
}

export async function streamGenerate(payload, { onDelta, onDone, onError }) {
  try {
    const res = await fetch(`${API}/generate/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (res.status === 402) {
      onError?.("Your free trial has ended. Upgrade to keep generating.", 402);
      return;
    }
    if (res.status === 401) {
      onError?.("Please sign in to continue.", 401);
      return;
    }
    if (res.status === 429) {
      const j = await res.json().catch(() => ({}));
      onError?.(j.detail || "Too many requests. Please wait a moment.", 429);
      return;
    }
    if (!res.ok || !res.body) {
      onError?.("Generation failed. Try again.");
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const parts = buf.split("\n\n");
      buf = parts.pop();
      for (const p of parts) {
        const line = p.trim();
        if (!line.startsWith("data:")) continue;
        const jsonStr = line.slice(5).trim();
        if (!jsonStr) continue;
        try {
          const evt = JSON.parse(jsonStr);
          if (evt.type === "delta") onDelta?.(evt.content);
          else if (evt.type === "done") onDone?.(evt.result);
          else if (evt.type === "error") onError?.(evt.detail);
        } catch (_) {}
      }
    }
  } catch (e) {
    onError?.(e.message || "Network error");
  }
}

// ---- cloud library ----
export const getLibraryApi = () => axios.get(`${API}/library`).then((r) => r.data);
export const saveLibraryApi = (result, scope = "personal") =>
  axios.post(`${API}/library?scope=${scope}`, result).then((r) => r.data);
export const shareToTeamApi = (id, shared) =>
  axios.post(`${API}/library/${id}/share`, { shared }).then((r) => r.data);

export const forgotPasswordApi = (email) => axios.post(`${API}/auth/forgot-password`, { email }).then((r) => r.data);
export const resetPasswordApi = (token, password) => axios.post(`${API}/auth/reset-password`, { token, password }).then((r) => r.data);
export const verifyEmailApi = (token) => axios.post(`${API}/auth/verify-email`, { token }).then((r) => r.data);
export const resendVerificationApi = () => axios.post(`${API}/auth/resend-verification`, {}).then((r) => r.data);

// ---- packs & follow-ups ----
export const getTemplatesApi = () => axios.get(`${API}/templates`).then((r) => r.data);
export const getDiwaliPackApi = () => axios.get(`${API}/packs/diwali`).then((r) => r.data);
export const claimDiwaliPackApi = () => axios.post(`${API}/packs/diwali/claim`, {}).then((r) => r.data);
export const clarifyApi = (input_text, target_tool) =>
  axios.post(`${API}/clarify`, { input_text, target_tool }).then((r) => r.data);

// ---- billing, referrals, revenue ----
export const myReceiptsApi = () => axios.get(`${API}/receipts`).then((r) => r.data);
export const myReferralsApi = () => axios.get(`${API}/referrals/me`).then((r) => r.data);
export const adminRevenueApi = () => axios.get(`${API}/admin/revenue`).then((r) => r.data);
export const adminAdminsApi = () => axios.get(`${API}/admin/admins`).then((r) => r.data);
export const adminAddAdminApi = (email) => axios.post(`${API}/admin/admins`, { email }).then((r) => r.data);
export const adminRemoveAdminApi = (email) => axios.delete(`${API}/admin/admins/${encodeURIComponent(email)}`).then((r) => r.data);
export const adminUsersApi = (q) => axios.get(`${API}/admin/users`, { params: { q } }).then((r) => r.data);
export const adminUserApi = (id) => axios.get(`${API}/admin/users/${id}`).then((r) => r.data);
export const adminGrantProApi = (id, days) => axios.post(`${API}/admin/users/${id}/grant-pro`, { days }).then((r) => r.data);
export const adminExtendTrialApi = (id, days) => axios.post(`${API}/admin/users/${id}/extend-trial`, { days }).then((r) => r.data);
export const adminRefundApi = (sessionId) => axios.post(`${API}/admin/payments/${sessionId}/refund`, {}).then((r) => r.data);

// ---- teams ----
export const myTeamApi = () => axios.get(`${API}/teams/me`).then((r) => r.data.team);
export const createTeamApi = (name) => axios.post(`${API}/teams`, { name }).then((r) => r.data.team);
export const joinTeamApi = (code) => axios.post(`${API}/teams/join`, { code }).then((r) => r.data.team);
export const leaveTeamApi = () => axios.post(`${API}/teams/leave`, {}).then((r) => r.data);
export const regenTeamCodeApi = () => axios.post(`${API}/teams/regenerate-code`, {}).then((r) => r.data.team);

// ---- admin ----
export const listReportsApi = (status) =>
  axios.get(`${API}/bug-reports`, { params: status ? { status } : {} }).then((r) => r.data);
export const getReportApi = (id) => axios.get(`${API}/bug-reports/${id}`).then((r) => r.data);
export const setReportStatusApi = (id, status) =>
  axios.patch(`${API}/bug-reports/${id}`, { status }).then((r) => r.data);
export const addVersionApi = (id, result) =>
  axios.post(`${API}/library/${id}/versions`, result).then((r) => r.data);
export const rollbackApi = (id, versionIndex) =>
  axios.post(`${API}/library/${id}/rollback`, { version_index: versionIndex }).then((r) => r.data);
export const deleteLibraryApi = (id) => axios.delete(`${API}/library/${id}`).then((r) => r.data);
export const importLibraryApi = (items) =>
  axios.post(`${API}/library/import`, { items }).then((r) => r.data);

// ---- payments ----
export const paymentStatus = (sessionId) =>
  axios.get(`${API}/payments/status/${sessionId}`).then((r) => r.data);
export const razorpayOrderApi = (packageId, interval = "monthly", currency = "inr") =>
  axios.post(`${API}/payments/razorpay/order`, { package_id: packageId, interval, currency }).then((r) => r.data);
export const getSaleApi = () => axios.get(`${API}/admin/sale`).then((r) => r.data);
export const setSaleApi = (body) => axios.put(`${API}/admin/sale`, body).then((r) => r.data);
export const razorpayVerifyApi = (payload) =>
  axios.post(`${API}/payments/razorpay/verify`, payload).then((r) => r.data);

// ---- bug reports ----
export const submitBugReport = (payload) =>
  axios.post(`${API}/bug-reports`, payload).then((r) => r.data);
